import { useApolloClient } from '@apollo/client';
import { useMemo } from 'react';

import type {
  AiStylesMetadataQuery,
  AiStylesMetadataQueryVariables,
  Maybe,
} from 'src/shared/graphql/generated';
import {
  AiStylesMetadataDocument,
  useAiStylesMetadataQuery,
} from 'src/shared/graphql/generated';

import type { StylesMetadata } from '../../types';

export interface UseStyleDescriptionOptions {
  style?: Maybe<string>;
  spaceType?: Maybe<string>;
}

export const useStyleDescription = (
  { style, spaceType }: UseStyleDescriptionOptions,
  skip: boolean = false,
) => {
  const client = useApolloClient();

  // Check in cache for metadata query results. If we have a cache hit, then we
  // ignore the skip parameter to allow an immediate value for the style
  // description to be returned
  const cachedStylesMetadata = client.readQuery<
    AiStylesMetadataQuery,
    AiStylesMetadataQueryVariables
  >({
    query: AiStylesMetadataDocument,
    variables: { spaceType: spaceType ?? '' },
  });

  const { data: stylesMetadata, loading } = useAiStylesMetadataQuery({
    variables: { spaceType: spaceType ?? '' },
    skip: !cachedStylesMetadata && (skip || !spaceType),
  });

  const description = useMemo(() => {
    if (!style) return '';

    // types as JSON from server
    const metadata = (stylesMetadata?.aiStylesMetadata?.metadata ??
      {}) as StylesMetadata;

    return metadata[style]?.description;
  }, [stylesMetadata, style]);

  return { description, loading };
};
